import { useEffect, useRef, useCallback, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { usePatientStore } from '../stores/patient';
import { toast } from 'sonner';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';
const WS_BASE_URL = import.meta.env.VITE_WS_URL || 'ws://localhost:8080';

interface QueueUpdatedPayload {
  locationId: string;
  totalWaiting: number;
  doctorsOnline: number;
}

interface PatientCalledPayload {
  queueEntryId: string;
  patientId: string;
  consultationId: string;
  roomUrl: string;
  token: string;
  doctorName?: string;
  doctorSpecialty?: string;
}

interface UseQueueOptions {
  onCalled?: (consultationData: {
    id: string;
    roomUrl: string;
    token: string;
    doctorName?: string;
    doctorSpecialty?: string;
  }) => void;
  onError?: (error: Error) => void;
  pollingInterval?: number;
  useWebSocket?: boolean;
}

export function useQueue(options: UseQueueOptions = {}) {
  const {
    onCalled,
    onError,
    pollingInterval = 5000,
    useWebSocket = true,
  } = options;

  const {
    patient,
    queueEntry,
    updateQueuePosition,
    updateQueueStatus,
    setConsultation,
    locationId,
  } = usePatientStore();

  const [isConnected, setIsConnected] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [connectionError, setConnectionError] = useState<Error | null>(null);

  const socketRef = useRef<Socket | null>(null);
  const pollingRef = useRef<number | null>(null);

  // Fetch queue position via HTTP (fallback)
  const fetchQueuePosition = useCallback(async () => {
    if (!queueEntry?.id) return;

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/queue/${locationId}/position/${queueEntry.id}`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch queue position: ${response.statusText}`);
      }

      const json = await response.json();
      // Unwrap API response envelope: { data: { status, position: { position, estimatedWaitMinutes, ... }, ... } }
      const data = json.data ?? json;

      // position is a nested object { position, totalWaiting, estimatedWaitMinutes }
      const pos = data.position;
      const numericPosition = typeof pos === 'object' ? pos.position : pos;
      const waitMinutes = typeof pos === 'object' ? pos.estimatedWaitMinutes : data.estimatedWaitMinutes;

      updateQueuePosition(numericPosition ?? 0, waitMinutes ?? 0);
      updateQueueStatus(data.status);

      // If called, set up consultation
      if (data.status === 'called' && data.consultationId && data.roomUrl) {
        setConsultation({
          id: data.consultationId,
          roomUrl: data.roomUrl,
          token: data.token,
          doctorName: data.doctorName,
          doctorSpecialty: data.doctorSpecialty,
        });

        onCalled?.({
          id: data.consultationId,
          roomUrl: data.roomUrl,
          token: data.token,
          doctorName: data.doctorName,
          doctorSpecialty: data.doctorSpecialty,
        });
      }

      setConnectionError(null);
    } catch (error) {
      const err = error instanceof Error ? error : new Error('Unknown error');
      setConnectionError(err);
      onError?.(err);
    }
  }, [queueEntry?.id, locationId, updateQueuePosition, updateQueueStatus, setConsultation, onCalled, onError]);

  // Start HTTP polling as fallback
  const startPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }
    pollingRef.current = window.setInterval(fetchQueuePosition, pollingInterval);
    console.log('[Queue] Started HTTP polling fallback');
  }, [fetchQueuePosition, pollingInterval]);

  // Stop HTTP polling
  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
      console.log('[Queue] Stopped HTTP polling');
    }
  }, []);

  // Connect to WebSocket using socket.io
  const connectWebSocket = useCallback(() => {
    if (!queueEntry?.id || !useWebSocket) return;

    // Disconnect existing socket
    if (socketRef.current) {
      socketRef.current.disconnect();
    }

    const socket = io(WS_BASE_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 3000,
      timeout: 20000,
    });

    socket.on('connect', () => {
      setIsConnected(true);
      setIsReconnecting(false);
      setConnectionError(null);
      console.log('[Queue] WebSocket connected');

      // Subscribe to location room for queue updates
      socket.emit('location:subscribe', locationId);

      // Subscribe to patient-specific room for called notifications
      socket.emit('patient:subscribe', { patientId: patient?.id, queueEntryId: queueEntry?.id });

      // Stop polling when WebSocket is connected
      stopPolling();
    });

    socket.on('disconnect', (reason) => {
      setIsConnected(false);
      console.log('[Queue] WebSocket disconnected:', reason);

      // Start polling as fallback
      startPolling();
    });

    socket.on('connect_error', (err) => {
      setConnectionError(new Error(`Connection error: ${err.message}`));
      console.error('[Queue] WebSocket connection error:', err);

      // Start polling as fallback
      if (!pollingRef.current) {
        startPolling();
      }
    });

    socket.on('reconnect_attempt', (attempt) => {
      setIsReconnecting(true);
      console.log('[Queue] Reconnection attempt:', attempt);
    });

    socket.on('reconnect', () => {
      setIsReconnecting(false);
      setIsConnected(true);
      console.log('[Queue] WebSocket reconnected');

      // Rejoin rooms after reconnection
      socket.emit('location:subscribe', locationId);
      socket.emit('patient:subscribe', { patientId: patient?.id, queueEntryId: queueEntry?.id });

      // Stop polling
      stopPolling();

      toast.success('Connection restored');
    });

    socket.on('reconnect_failed', () => {
      setIsReconnecting(false);
      setConnectionError(new Error('Failed to reconnect'));
      console.error('[Queue] WebSocket reconnection failed');
    });

    // Listen for queue updates
    socket.on('queue:updated', (data: QueueUpdatedPayload) => {
      console.log('[Queue] Queue updated:', data);
      // Refresh position on queue changes
      fetchQueuePosition();
    });

    // Listen for patient called event
    socket.on('patient:called', (data: PatientCalledPayload) => {
      console.log('[Queue] Patient called:', data);

      // Check if this is for us
      if (data.patientId === patient?.id || data.queueEntryId === queueEntry?.id) {
        updateQueueStatus('called');

        setConsultation({
          id: data.consultationId,
          roomUrl: data.roomUrl,
          token: data.token,
          doctorName: data.doctorName,
          doctorSpecialty: data.doctorSpecialty,
        });

        onCalled?.({
          id: data.consultationId,
          roomUrl: data.roomUrl,
          token: data.token,
          doctorName: data.doctorName,
          doctorSpecialty: data.doctorSpecialty,
        });

        toast.success("It's your turn! A doctor is ready to see you.");
      }
    });

    // Listen for consultation cancelled — patient goes back to waiting
    socket.on('consultation:cancelled', (data: { consultationId: string }) => {
      console.log('[Queue] Consultation cancelled:', data);
      const currentConsultation = usePatientStore.getState().consultation;
      if (data.consultationId === currentConsultation?.id) {
        // Clear consultation data, revert to waiting status
        setConsultation(null);
        updateQueueStatus('waiting');
        fetchQueuePosition();
        toast.info('Your consultation was cancelled. You have been returned to the queue.');
      }
    });

    // Listen for position updates
    socket.on('queue:position', (data: { position: number; estimatedWaitMinutes: number }) => {
      console.log('[Queue] Position update:', data);
      updateQueuePosition(data.position, data.estimatedWaitMinutes);
    });

    socketRef.current = socket;
  }, [
    queueEntry?.id,
    patient?.id,
    locationId,
    useWebSocket,
    fetchQueuePosition,
    updateQueueStatus,
    updateQueuePosition,
    setConsultation,
    onCalled,
    startPolling,
    stopPolling,
  ]);

  // Leave queue
  const leaveQueue = useCallback(async () => {
    const patientId = usePatientStore.getState().patient?.id;
    if (!patientId) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/queue/leave/${patientId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to leave queue');
      }

      // Disconnect socket
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }

      toast.success('You have left the queue');
    } catch (error) {
      console.error('[Queue] Failed to leave queue:', error);
      toast.error('Failed to leave queue. Please try again.');
      throw error;
    }
  }, [queueEntry?.id]);

  // Initialize connection
  useEffect(() => {
    if (!queueEntry?.id) return;

    // Initial fetch
    fetchQueuePosition();

    if (useWebSocket) {
      connectWebSocket();
    } else {
      // Use polling if WebSocket is disabled
      startPolling();
    }

    return () => {
      // Cleanup
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      stopPolling();
    };
  }, [queueEntry?.id, useWebSocket, fetchQueuePosition, connectWebSocket, startPolling, stopPolling]);

  return {
    position: queueEntry?.position ?? 0,
    estimatedWaitMinutes: queueEntry?.estimatedWaitMinutes ?? 0,
    status: queueEntry?.status ?? 'waiting',
    isConnected,
    isReconnecting,
    connectionError,
    leaveQueue,
    refresh: fetchQueuePosition,
  };
}
